/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, signal} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';
import template from './advertiser-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {AdvertiserInviteEditorModalComponent} from './advertiser-invite-editor-modal.component';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {IAB_CATEGORIES_V1} from '../../components/util/iab-categories-v1';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {MoneyHelper} from '../../components/util/money';
import {
  convertToApiBudgets,
  convertToApiEntities,
  convertToUiBudgets,
  convertToUiEntities,
  doTheCopyOfEntity
} from '../base/nude/advertiser-budgets';
import {buildElectionFormOptions, buildElectionSelectGroups, getElectionLabel} from '../../components/util/election-form-options';
import {linkedPoliticalCampaignRefs} from '../../components/util/political-campaign-advertiser-topics';
import {TeamApiService} from '../admin/team/team-api.service';
import {EmailTranslationApiService} from '../admin/email-translation/email-translation-api.service';
import {PublicPoliticalCampaignApiService} from '../political-campaign/public-political-campaign-api.service';
import {AdvertiserApiService, AdvertiserModel} from './advertiser-api.service';
import {UserApiService} from '../admin/user/user-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {BrowserLocationService} from '../angular-platform-providers';
import {deferSignalStateWrite} from '../common/util/template-state';
import {compareById} from '../common/util/object-id-comparison';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';

function resolveElection(election: any, localNetworkInfo: LocalNetworkInfo): any {
  if (!election) {
    return null;
  }
  const networkElections = localNetworkInfo.getSuppInfo('elections') || [];
  if (_.isString(election)) {
    return _.find(networkElections, (candidate: any) => {
      return candidate.id === election || candidate.name === election;
    }) || {id: election, name: election};
  }
  const networkElection = _.find(networkElections, (candidate: any) => {
    return (_.get(election, 'id') && candidate.id === election.id)
      || (_.get(election, 'name') && candidate.name === election.name);
  });
  return _.assign({}, networkElection || {}, election);
}

function formatElectionLabel(election: any, localNetworkInfo: LocalNetworkInfo): string {
  const resolvedElection = resolveElection(election, localNetworkInfo);
  return resolvedElection ? getElectionLabel(resolvedElection) : '';
}

function politicalCampaignDetail(ref: any, campaign: any, localNetworkInfo: LocalNetworkInfo): any {
  return {
    publicId: ref.publicId,
    id: ref.id || campaign.id,
    name: ref.name || campaign.name,
    campaign: campaign,
    electionLabel: formatElectionLabel(campaign.election, localNetworkInfo),
    expanded: false
  };
}

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule],
  selector: 'adn-advertiser-edit-page',
  template
})
export class AdvertiserEditPageComponent extends SignalAdminResourceEditPageBase<AdvertiserModel> {

  private readonly advertiserInfoState = signal(false);
  private readonly teamsState = signal<any[]>([]);
  private readonly viewOnlyTeamsState = signal<any[]>([]);
  private readonly usersState = signal<any[]>([]);
  private readonly userSearchState = signal(false);
  private readonly entitiesState = signal<any[]>([]);
  private readonly electionsState = signal<any[]>([]);
  private readonly electionSelectGroupsState = signal<any>({dated: [], undated: [], useGroups: false, groups: []});
  private readonly politicalCampaignDetailsState = signal<any[]>([]);
  private readonly politicalCampaignDetailsResolvedState = signal(false);
  private readonly hasLinkedPoliticalCampaignsState = signal(false);
  private readonly isAxAdvertiserAdminState = signal(false);
  private readonly canSetPoliticalState = signal(false);
  private readonly canSeeEditorInviteState = signal(false);
  private readonly currentPoliticalInviteState = signal<any>(null);
  private readonly politicalInviteExpiringState = signal(false);
  private readonly politicalInviteErrorState = signal('');
  private readonly blockSubmitState = signal(false);

  get advertiserInfo(): boolean { return this.advertiserInfoState(); }
  set advertiserInfo(value: boolean) { this.advertiserInfoState.set(value); }
  get teams(): any[] { return this.teamsState(); }
  set teams(value: any[]) { this.teamsState.set(value); }
  get viewOnlyTeams(): any[] { return this.viewOnlyTeamsState(); }
  set viewOnlyTeams(value: any[]) { this.viewOnlyTeamsState.set(value); }
  get users(): any[] { return this.usersState(); }
  set users(value: any[]) { this.usersState.set(value); }
  get userSearch(): boolean { return this.userSearchState(); }
  set userSearch(value: boolean) { this.userSearchState.set(value); }
  get entities(): any[] { return this.entitiesState(); }
  set entities(value: any[]) { this.entitiesState.set(value); }
  get elections(): any[] { return this.electionsState(); }
  set elections(value: any[]) { this.electionsState.set(value); }
  get electionSelectGroups(): any { return this.electionSelectGroupsState(); }
  set electionSelectGroups(value: any) { this.electionSelectGroupsState.set(value); }
  get politicalCampaignDetails(): any[] { return this.politicalCampaignDetailsState(); }
  set politicalCampaignDetails(value: any[]) { this.politicalCampaignDetailsState.set(value); }
  get politicalCampaignDetailsResolved(): boolean { return this.politicalCampaignDetailsResolvedState(); }
  set politicalCampaignDetailsResolved(value: boolean) { this.politicalCampaignDetailsResolvedState.set(value); }
  get hasLinkedPoliticalCampaigns(): boolean { return this.hasLinkedPoliticalCampaignsState(); }
  set hasLinkedPoliticalCampaigns(value: boolean) { this.hasLinkedPoliticalCampaignsState.set(value); }
  get isAxAdvertiserAdmin(): boolean { return this.isAxAdvertiserAdminState(); }
  set isAxAdvertiserAdmin(value: boolean) { this.isAxAdvertiserAdminState.set(value); }
  get canSetPolitical(): boolean { return this.canSetPoliticalState(); }
  set canSetPolitical(value: boolean) { this.canSetPoliticalState.set(value); }
  get canSeeEditorInvite(): boolean { return this.canSeeEditorInviteState(); }
  set canSeeEditorInvite(value: boolean) { this.canSeeEditorInviteState.set(value); }
  get currentPoliticalInvite(): any { return this.currentPoliticalInviteState(); }
  set currentPoliticalInvite(value: any) { this.currentPoliticalInviteState.set(value); }
  get politicalInviteExpiring(): boolean { return this.politicalInviteExpiringState(); }
  set politicalInviteExpiring(value: boolean) { this.politicalInviteExpiringState.set(value); }
  get politicalInviteError(): string { return this.politicalInviteErrorState(); }
  set politicalInviteError(value: string) { this.politicalInviteErrorState.set(value); }
  get blockSubmit(): boolean { return this.blockSubmitState(); }
  set blockSubmit(value: boolean) { this.blockSubmitState.set(value); }

  readonly entityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];

  suiPlatform = false;
  isMarketplace = false;
  canSeeTechFees = false;
  isSpecialUser = false;
  showTeams = false;
  hasAdvertiserEnforced = false;
  networkCurrencies: string[] = [];
  availableCurrencies: string[] = [];
  iabTaxonomy: any;
  iabCategoriesV1: any;
  teamsWarning: any;

  private defaultAdvertiserInviteSubject = 'You are invited to update Advertiser details';
  private username: string;

  constructor(
    private readonly advertiserApi: AdvertiserApiService,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly angularModalService: AngularModalService,
    private readonly publicPoliticalCampaignApi: PublicPoliticalCampaignApiService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly emailTranslationApi: EmailTranslationApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly userApi: UserApiService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(advertiserApi, 'advertiser');

    this.suiPlatform = LocalNetworkProfile.isSuiPlatform();
    this.isMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.canSeeTechFees = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalNetworkProfile.isPureMarketplacePlatform()
      || LocalNetworkProfile.hasAdvertiserEnforced();

    this.username = localUserProfile.get('username');
    const isLocalhost = this.location.hostname === 'localhost';
    this.isSpecialUser = (isLocalhost && this.username.startsWith('admin@'))
      || this.username === 'adnuntius@adnuntius.com'
      || this.username === 'broker1@adnuntius.com';

    this.showTeams = this.localNetworkInfo.getInfo('teamsCount') > 1 || !LocalNetworkProfile.hasAdvertiserEnforced();
    this.hasAdvertiserEnforced = LocalNetworkProfile.hasAdvertiserEnforced();

    const defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });

    this.availableCurrencies = ['DKK', 'EUR', 'NOK', 'SEK'];
    if (this.location.hostname === 'localhost') {
      this.availableCurrencies.push('AUD');
    }

    const re = /\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/;
    this.emailTranslationApi.listOptions({
      showEditableOnly: true,
      filterByParam: 'messageDefinition',
      filterByValue: 'ADVERTISER_UPDATE_INVITE'
    }).subscribe((data) => {
      _.forEach(data?.results || [], (result: any) => {
        if (result.validationWarnings.length === 0) {
          this.defaultAdvertiserInviteSubject = _.get(result.emailSubject.match(re), [1], '') || this.defaultAdvertiserInviteSubject;
        }
      });
    });

    this.iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);
    this.iabCategoriesV1 = _.cloneDeep(IAB_CATEGORIES_V1);

    this.teamApi.listOptions({pageSize: 10001}).subscribe((page) => {
      // [] → results flips *ngFor on the owner-team <select> mid-CD (NG0100).
      deferSignalStateWrite(() => {
        const allTeams = page.results;
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        const firstFilterTeams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams = _.filter(firstFilterTeams, (t: any) => {
          return t.type === 'AX_ADVERTISER' || t.type === 'STANDARD' || t.type === 'SUI';
        });
        this.viewOnlyTeams = _.filter(firstFilterTeams, (t: any) => {
          return t.type === 'AX_ADVERTISER' || t.type === 'STANDARD' || t.type === 'CREATIVE_AGENCY';
        });
        this.resolveOwnerTeamReference();
        if ((this.isAxAdvertiserAdmin || this.hasAdvertiserEnforced) && allTeams.length > 0 && this.teams.length > 0 && !this.editingModel.ownerTeam) {
          this.editingModel.ownerTeam = this.editingModel.ownerTeam || this.teams[0];
        }
      });
    });
  }

  editsMade(): void {
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  readonly compareById = compareById;

  trackByIndex(index: number): number {
    return index;
  }

  teamChanged(): void {
    this.updateUsers();
    this.onFieldChange();
  }

  addElectionBudget(): void {
    this.editingModel.uiPoliticalBudgets = this.editingModel.uiPoliticalBudgets || [];
    this.editingModel.uiPoliticalBudgets.push({});
    this.editsMade();
  }

  removeElection(index: number): void {
    _.pullAt(this.editingModel.uiPoliticalBudgets, index);
    this.editsMade();
  }

  removeCampaign(budget: any, index: number): void {
    _.pullAt(budget.campaigns, index);
    this.editsMade();
  }

  addCampaign(budget: any): void {
    budget.campaigns = budget.campaigns || [];
    budget.campaigns.push({campaignName: '', budget: {}});
    this.editsMade();
  }

  inviteEditor = (): void => {
    const modalRef = this.angularModalService.show(AdvertiserInviteEditorModalComponent, {
      class: 'modal-md',
      initialState: {
        advertiserId: this.editingModel.id,
        defaultSubject: this.defaultAdvertiserInviteSubject
      }
    });
    modalRef.onHidden.subscribe(() => this.loadCurrentPoliticalInvite());
  };

  expirePoliticalInvite(): void {
    if (!this.currentPoliticalInvite || this.politicalInviteExpiring) {
      return;
    }
    this.politicalInviteExpiring = true;
    this.politicalInviteError = '';
    this.advertiserApi.expirePoliticalEntityInvite({
      id: this.editingModel.id,
      expiry: new Date(0).toISOString()
    }).subscribe({next: () => {
      this.politicalInviteExpiring = false;
      this.currentPoliticalInvite = null;
    }, error: (response: any) => {
      this.politicalInviteExpiring = false;
      this.politicalInviteError = response.data?.text || 'The linkage request could not be cancelled.';
    }});
  }

  removeLayoutFee = (index: number): void => {
    _.pullAt(this.editingModel.uiLayoutFees, index);
    if (this.editingModel.uiLayoutFees.length === 0) {
      this.addLayoutFee();
    }
    this.editsMade();
  };

  copyToEntity = (entity: any): void => {
    this.editsMade();
    doTheCopyOfEntity(this.editingModel, entity);
  };

  addLayoutFee = (): void => {
    this.editingModel.uiLayoutFees = this.editingModel.uiLayoutFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    this.initFeesForParam(defaultFee.fee);
    this.editingModel.uiLayoutFees.push(defaultFee);
    this.editsMade();
  };

  togglePoliticalCampaignDetail(detail: any): void {
    detail.expanded = !detail.expanded;
  }

  get showReadonlyLayoutFees(): boolean {
    return this.hasAdvertiserEnforced && !!_.get(this.editingModel, ['uiLayoutFees', 0, 'layout', 'name']);
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.teams = _.map(this.editingModel.teams || [], (team: any) => _.isString(team) ? team : team.id);
    this.editingModel.viewOnlyTeams = _.map(this.editingModel.viewOnlyTeams || [], (team: any) => _.isString(team) ? team : team.id);
    this.editingModel.address = this.editingModel.address || {country: ''};
    this.editingModel.type = this.editingModel.type || null;
    this.advertiserInfo = !!this.isNew;
    this.blockSubmit = !!this.readOnly;
    this.resolveOwnerTeamReference();
    this.initialiseLayoutFees();
    this.updateUsers();
    this.refreshPoliticalState();
    this.loadCurrentPoliticalInvite();
  }

  protected override prepareSubmitPayload(payload: AdvertiserModel): void {
    payload.layoutFees = _.filter(payload.uiLayoutFees, (layoutFee: any) => {
      return _.get(layoutFee, ['layout', 'id']) && _.filter(_.map(_.get(layoutFee, ['fee', 'multiCurrencyMonetaryAmounts']), (m: any) => {
        return MoneyHelper.fixMoney(m);
      }), (f: any) => {
        return MoneyHelper.isMoney(f);
      }).length > 0;
    });
    payload.layoutFees = _.map(payload.layoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee);
      adjFee.fee.multiCurrencyMonetaryAmounts = _.filter(_.map(adjFee.fee.multiCurrencyMonetaryAmounts, (m: any) => {
        return MoneyHelper.fixMoney(m);
      }), (f: any) => {
        return MoneyHelper.isMoney(f);
      });
      return adjFee;
    });
    payload.politicalBudgets = convertToApiBudgets(payload.uiPoliticalBudgets);
    convertToApiEntities(payload);

    if (!payload.enablePolitical) {
      payload.type = null;
    }
  }

  private refreshPoliticalState(saveObj?: any): void {
    const ownerTeam = this.editingModel.ownerTeam;
    const ownerTeamId = _.isString(ownerTeam) ? ownerTeam : _.get(ownerTeam, 'id');

    this.isAxAdvertiserAdmin = this.LocalUserPermissions.hasNetwork('AX_ADVERTISER_ADMIN')
      || !!(ownerTeamId && this.LocalUserPermissions.has('AX_ADVERTISER_TEAM_ADMIN', ownerTeamId));

    this.canSetPolitical = this.LocalUserPermissions.hasNetwork('MANAGE_BUSINESS') || this.isAxAdvertiserAdmin;
    this.canSeeEditorInvite = !!(this.editingModel.ownerTeam && this.canSetPolitical);

    const workingObj = saveObj || this.editingModel;
    this.editingModel.uiPoliticalBudgets = convertToUiBudgets(workingObj);
    convertToUiEntities(workingObj);
    this.entities = [this.editingModel.controllingEntity, this.editingModel.payingEntity];
    this.editingModel.enablePolitical = !!this.editingModel.type;
    this.elections = buildElectionFormOptions(this.localNetworkInfo.getSuppInfo('elections') || [], {
      username: this.username,
      canSeeTestElections: this.localUserProfile.canSeeTestElections(),
      selectedElectionIds: _.map(this.editingModel.uiPoliticalBudgets || [], (budget: any) => {
        return _.get(budget, ['election', 'id']);
      }),
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);
    this.loadPoliticalCampaignDetails();
  }

  private loadCurrentPoliticalInvite(): void {
    if (!this.canSeeEditorInvite || !this.editingModel?.id || this.isNew) {
      this.currentPoliticalInvite = null;
      this.politicalInviteError = '';
      return;
    }
    this.advertiserApi.currentPoliticalEntityInvite(this.editingModel.id).subscribe({next: (response: any) => {
      this.currentPoliticalInvite = response.invitation || null;
      this.politicalInviteError = '';
    }, error: () => {
      this.currentPoliticalInvite = null;
    }});
  }

  private initFeesForParam(ref: any): void {
    _.forEach(this.networkCurrencies, (currency: string) => {
      const foundFee = _.find(_.get(ref, 'multiCurrencyMonetaryAmounts', []), (fee: any) => {
        return fee.currency === currency;
      });
      if (!foundFee) {
        const monetary = _.get(ref, 'monetary');
        if (monetary && monetary.currency === currency) {
          ref.type = 'MULTI_CURRENCY_MONETARY';
          ref.multiCurrencyMonetaryAmounts = ref.multiCurrencyMonetaryAmounts || [];
          ref.multiCurrencyMonetaryAmounts.push(monetary);
        } else {
          ref.multiCurrencyMonetaryAmounts = ref.multiCurrencyMonetaryAmounts || [];
          ref.multiCurrencyMonetaryAmounts.push({currency: currency});
        }
      }
    });
    ref.multiCurrencyMonetaryAmounts = _.orderBy(ref.multiCurrencyMonetaryAmounts, (fee: any) => {
      return this.networkCurrencies.indexOf(fee.currency);
    });
  }

  private initialiseLayoutFees(): void {
    const layoutFees = _.get(this.editingModel, 'layoutFees');
    this.editingModel.uiLayoutFees = _.isArray(layoutFees) ? layoutFees : [];
    this.editingModel.uiLayoutFees[0] = this.editingModel.uiLayoutFees[0] ? this.editingModel.uiLayoutFees[0] : {};
    this.editingModel.uiLayoutFees = _.map(this.editingModel.uiLayoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee) || {};
      adjFee.fee = adjFee.fee || {type: 'MULTI_CURRENCY_MONETARY'};
      this.initFeesForParam(adjFee.fee);
      return adjFee;
    });
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.teams.length) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.teams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }

  private updateUsers(): void {
    this.userSearch = false;
    if (this.suiPlatform && _.get(this.editingModel, ['ownerTeam', 'id'])) {
      this.userSearch = true;
      this.userApi.listOptions({selfServiceOnly: true, team: this.editingModel.ownerTeam.id}).subscribe((page) => {
        deferSignalStateWrite(() => {
          this.users = page.results;
          this.userSearch = false;
        });
      }, () => {
        deferSignalStateWrite(() => {
          this.userSearch = false;
        });
      });
    }
  }

  private loadPoliticalCampaignDetails(): void {
    const linkedCampaigns = linkedPoliticalCampaignRefs(this.editingModel);
    this.hasLinkedPoliticalCampaigns = linkedCampaigns.length > 0;
    if (!this.hasLinkedPoliticalCampaigns) {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.politicalCampaignDetails = [];
        this.politicalCampaignDetailsResolved = true;
      });
      return;
    }
    this.politicalCampaignDetailsResolved = false;
    Promise.all(_.map(linkedCampaigns, (ref: any) => {
      return firstValueFrom(this.publicPoliticalCampaignApi.get(ref.publicId)).then((campaign: any) => {
        return politicalCampaignDetail(ref, campaign, this.localNetworkInfo);
      }, () => {
        return {
          publicId: ref.publicId,
          id: ref.id,
          name: ref.name,
          loadError: true,
          expanded: false
        };
      });
    })).then((results: any[]) => {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.politicalCampaignDetails = results;
        this.politicalCampaignDetailsResolved = true;
      });
    });
  }
}
